import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './Chat.css';

const TenantChat = () => {
  const { chatId, propertyId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState(null);
  const messagesEndRef = useRef(null);
  
  const currentUser = JSON.parse(localStorage.getItem('user'))?.user;
  const token = localStorage.getItem('token');
  const base_url = 'https://api.homehive.co.zw/api';

  const chatContainerStyle = {
    backgroundImage: 'url("/images/whatsapp-bg.png")', // A WhatsApp-like background pattern
    backgroundRepeat: 'repeat',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#efeae2', // WhatsApp's background color
  };

  // Authentication check
  useEffect(() => {
    if (!currentUser || !token) {
      navigate('/login');
      return;
    }

    if (currentUser.is_landlord) {
      navigate('/');
      Swal.fire({
        title: 'Unauthorized',
        text: 'This area is for tenants only',
        icon: 'error',
        confirmButtonColor: '#075E54'
      });
      return;
    }
  }, [currentUser, token, navigate]);

  // Create or fetch conversation
  useEffect(() => {
    const initializeConversation = async () => {
      if (!token || (!chatId && !propertyId)) return;

      try {
        setLoading(true);
        
        if (chatId) {
          const response = await axios.get(
            `${base_url}/conversations/${chatId}/`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          if (!response.data?.conversation) {
            throw new Error('No conversation data received');
          }

          setConversation(response.data.conversation);
          // Sort messages by timestamp, oldest first (WhatsApp style)
          const sortedMessages = response.data.messages ? 
            [...response.data.messages].sort(
              (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
            ) : [];
          setMessages(sortedMessages);
        } else if (propertyId) {
          // Start new conversation about property
          const response = await axios.post(
            `${base_url}/conversations/`,
            {
              property_id: propertyId
            },
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          setConversation(response.data);
          setMessages([]);
          // Update URL with new conversation ID
          navigate(`/chat/${response.data.id}`, { replace: true });
        }
        
        setLoading(false);
        scrollToBottom();
      } catch (err) {
        console.error('Error in component:', err);
        setError(err.response?.data?.message || 'Failed to load conversation');
        setLoading(false);
      }
    };

    initializeConversation();
  }, [chatId, propertyId, token, navigate]);

  // Poll for new messages
  useEffect(() => {
    if (!conversation?.id) return;

    const pollMessages = setInterval(async () => {
      try {
        const response = await axios.get(
          `${base_url}/conversations/${conversation.id}/`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        // Sort messages by timestamp, oldest first (WhatsApp style)
        const sortedMessages = response.data.messages ? 
          [...response.data.messages].sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          ) : [];
        setMessages(sortedMessages);
        scrollToBottom(); // Scroll to bottom when new messages arrive
      } catch (error) {
        console.error('Error polling messages:', error);
      }
    }, 5000);

    return () => clearInterval(pollMessages);
  }, [conversation?.id, token]);

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !token || !conversation) return;

    try {
      const response = await axios.post(
        `${base_url}/conversations/${conversation.id}/messages/`,
        { content: newMessage },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Add new message at the end (WhatsApp style)
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      scrollToBottom();
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to send message. Please try again.',
        icon: 'error',
        confirmButtonColor: '#075E54'
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Find landlord (other participant)
  const landlord = React.useMemo(() => {
    if (!conversation?.participants) return null;
    return conversation.participants.find(p => p.id !== currentUser?.id);
  }, [conversation, currentUser]);

  const formatName = (participant) => {
    if (!participant) return 'Landlord';
    return participant.name || participant.email?.split('@')[0] || 'Landlord';
  };

  if (loading) {
    return (
      <div className="chat-container" style={chatContainerStyle}>
        <div className="chat-loading">
          <i className="fas fa-spinner fa-spin"></i> Loading chat...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-container" style={chatContainerStyle}>
        <div className="chat-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container" style={chatContainerStyle}>
      <div className="chat-header fixed">
        <div className="chat-header-content">
          <div className="header-main">
            <button 
              className="back-button" 
              onClick={() => navigate('/conversations')}
            >
              <i className="fas fa-arrow-left"></i>
            </button>
            <div style={{marginLeft: '40px', marginTop: '-20px'}} className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="chat-info">
              {conversation && (
                <>
                  <h2 style={{marginTop: '-20px'}} className="chat-name">
                    {landlord ? formatName(landlord) : 'Loading...'}
                  </h2>
                  {conversation.property && (
                    <div style={{fontSize: '12px'}} className="property-info">
                      <span><i className="fas fa-home"></i> {conversation.property.property_type_display}</span>
                      <span>•</span>
                      <span><i className="fas fa-map-marker-alt"></i> {conversation.property.location}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="messages-wrapper">
        <div className="messages-container">
          {messages.length === 0 ? (
            <div className="no-messages">
              <div className="no-messages-content">
                <i className="fas fa-comments"></i>
                <p>No messages yet</p>
                <span>Start the conversation!</span>
              </div>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((message, index) => {
                const isSentByMe = message.sender.id === currentUser?.id;
                const showTimestamp = index === 0 || 
                  new Date(message.timestamp).getTime() - 
                  new Date(messages[index - 1]?.timestamp).getTime() > 300000;
                const isLastInGroup = 
                  index === messages.length - 1 || 
                  messages[index + 1]?.sender.id !== message.sender.id;

                return (
                  <div
                    key={message.id}
                    className={`message ${isSentByMe ? 'sent' : 'received'} ${isLastInGroup ? 'last-in-group' : ''}`}
                  >
                    <div className="message-content">
                      <p>{message.content}</p>
                      <div className="message-meta">
                        <small className="message-timestamp">
                          {new Date(message.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </small>
                        {isSentByMe && <i className="fas fa-check message-status"></i>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      <form onSubmit={sendMessage} className="message-form fixed">
        <div className="message-input-container">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message"
            className="message-input"
          />
        </div>
        <button 
          type="submit" 
          className="send-button" 
          disabled={!newMessage.trim()}
        >
          <i className="fas fa-paper-plane"></i>
        </button>
      </form>
    </div>
  );
};

export default TenantChat;
